"use client";
import React, { useState } from "react";
import Button from "../../../ui/Button";
import { GroupHeader } from "../../../ui/Headers";
import Link from "next/link";
import {
  mockProperties,
  PropertyTypeKey,
} from "../../../constants/mockProperties";
import { PropertyType } from "../../../../../types";
import CardGrid from "src/app/components/CardGrid";
import { getCurrencySign } from "src/app/lib/getCurrencySign";
import { PropertyCardSkeleton } from "../../design2/components/Skeletons/Design2PropertyCard";
import Image from "next/image";
import { generateSlugWithId } from "src/app/serverUtils";
// import { useRouteLoadingReset } from "src/app/components/hooks/useRouterLoading";

interface Tab {
  label: React.ReactNode; // Allows JSX elements
  key: PropertyTypeKey;
  url: string;
}

const tabs: Tab[] = [
  {
    label: (
      <>
        <span className=" xs:hidden">Property </span>For Rent
      </>
    ),
    url: "/properties/rent",
    key: "forRent",
  },
  {
    label: (
      <>
        <span className=" xs:hidden">Property </span>For Sale
      </>
    ),
    url: "/properties/sale",
    key: "forSale",
  },
  {
    label: (
      <>
        <span className=" xs:hidden">Property </span>For Short Let
      </>
    ),
    url: "/properties/shortlet",
    key: "forShortlet",
  },
];

const tabs2: Tab[] = [
  {
    label: <>For Rent</>,
    url: "/properties/rent",
    key: "forRent",
  },
  {
    label: <>For Sale</>,
    url: "/properties/sale",
    key: "forSale",
  },
  {
    label: <>Short Let</>,
    url: "/properties/shortlet",
    key: "forShortlet",
  },
];

export function camelCaseToWords(camelCaseStr: string): string {
  return camelCaseStr
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
    .replace(/^./, (char) => char.toUpperCase()); // Capitalize the first letter
}

export interface TabsProps {
  tabs: Tab[];
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
}

export const getTypeColor = (type: "sale" | "rent" | "short let"): string => {
  const typeColors: Record<"sale" | "rent" | "short let", string> = {
    rent: "bg-[#E15B0F] text-white",
    sale: "bg-[#5A7EEC] text-white",
    "short let": "bg-[#FBA92D] text-white",
  };

  return typeColors[type]; // No fallback needed since type is strictly defined
};

const Tabs: React.FC<TabsProps> = ({ tabs, activeIndex, setActiveIndex }) => {
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";

  return (
    <div className="flex scrollbar-hide overflow-auto w-max mx-auto gap-4 mt-6">
      {tabs.map((tab, index) => {
        const isDisabled =
          !isDemo && (tab.key === "forSale" || tab.key === "forShortlet");
        return (
          <Button
            key={tab.key}
            onClick={() => !isDisabled && setActiveIndex(index)}
            className={`px-4 py-3 xs:px-3 xs:text-sm rounded-lg transition ${
              activeIndex === index
                ? "bg-themeColor primary-btn text-white"
                : "bg-transparent !text-themeColor border border-themeColor"
            } ${isDisabled ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={isDisabled}
          >
            {tab.label}
          </Button>
        );
      })}
    </div>
  );
};

const Listing: React.FC<{ variant?: boolean; properties: PropertyType[] }> = ({
  variant,
  properties,
}) => {
  const isDemoMode = process.env.NEXT_PUBLIC_DEMO_MODE === "true"; // Check if DEMO_MODE is true
  const isLoading = false;

  // Ensure data is available and slice the first 3 properties
  const realProperties = properties ? [...properties.slice(0, 4)] : [];
  const wrappedProperties = {
    forRent: realProperties,
    forShortlet: realProperties,
    forSale: realProperties,
  };

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const activeKey: PropertyTypeKey = tabs[activeIndex].key;
  const currentProperties: PropertyType[] = isDemoMode
    ? mockProperties[activeKey] ?? []
    : wrappedProperties[activeKey] ?? [];

  const url = tabs[activeIndex].url;
  const [routing, setRouting] = useState(false);
  const loading = isLoading && !isDemoMode; // Check if loading and not in demo mode

  return (
    <section className="py-8 pt-12 overflow-hidden">
      <div className="wrapper">
        <GroupHeader
          max={1024}
          subheader={
            variant
              ? "Browse the newest properties available, featuring premium listings updated in real time."
              : "Discover your dream property with ease-buy, sell, or rent from trusted listings tailored to your needs."
          }
          header={
            variant ? "Latest Property Listings" : "Recent Properties Listings"
          }
        />

        <Tabs
          tabs={variant ? tabs2 : tabs}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
        />

        <div className="mt-6 py-4 ">
          {loading ? (
            <CardGrid minCardWidth={250} maxColumns={4}>
              {Array.from({ length: 4 }, (_, index) => (
                <PropertyCardSkeleton key={index} />
              ))}
            </CardGrid>
          ) : currentProperties.length > 0 ? (
            <CardGrid
              minCardWidth={variant ? 300 : 350}
              maxColumns={variant ? 4 : 2}
            >
              {variant
                ? currentProperties.map((property) => (
                    <VariantCard
                      className="!w-full"
                      key={property.id}
                      {...property}
                    />
                  ))
                : currentProperties.map((property) => (
                    <Card key={property.id} {...property} />
                  ))}
              {/* {currentProperties.map((property) => (
                <Card key={property.id} {...property} />
              ))} */}
            </CardGrid>
          ) : (
            <p className="text-center mb-10 text-gray-500">
              No properties available.
            </p>
          )}

          {currentProperties.length > 0 && (
            <div onClick={() => setRouting(true)}>
              <Button
                href={url}
                className=" primary-btn !px-8 !font-normal text-[14px]  py-3 block mt-14 !text-white w-max mx-auto"
              >
                {routing ? "Loading" : "Explore More"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
export default Listing;
export const Card: React.FC<PropertyType> = ({
  images,
  title,
  address,
  reviews,
  currency,
  videoLink,
  size,
  price,
  type,
  beforePrice,
  strike,
  id,
  quote,
  quotePrice,
  priceType,
  category,
}) => {
  const slugwithid = generateSlugWithId(Number(id), title);
  const baseUrl = `/properties/${
    type === "short let" ? "shortlet" : type
  }/${slugwithid}`;
  const formattedCurrency = getCurrencySign(currency);

  return (
    <Link
      href={baseUrl}
      style={{
        boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
      }}
      className="flex mx-auto  text-primary bg-white border overflow-hidden rounded-2xl"
    >
      <div className="first group relative w-[43%]">
        <div className="hover group-hover:flex hidden transition-all duration-500 absolute bg-black/60 items-center justify-center z-10 top-0 left-0 w-full h-full">
          <Button className="bg-white  !text-themeColor w-max h-max">
            View Details
          </Button>
        </div>
        <div
          className="bottom text-black absolute flex 
        gap-2 bottom-4 right-4"
        >
          <span className="bg-brand-light flex gap-1 items-center rounded-sm p-[0.3rem] py-[0.2rem] text-[8px]">
            <CameraIcon /> +{images.length}
          </span>
          {videoLink && (
            <span className="bg-brand-light flex gap-2 items-center rounded-sm p-[0.4rem] py-[0.2rem] text-[8px]">
              <VideoIcon />
            </span>
          )}
        </div>
        <div
          className={`tag absolute capitalize w-[180px] text-center text-[11px] left-[-70px] transform origin-center rotate-[-40deg] top-[4%] ${getTypeColor(
            type
          )}`}
        >
          <span>{type !== "short let" && "For"}</span> {type}
        </div>
        <Image
          height={200}
          width={200}
          src={images[0]}
          alt={title}
          className="w-full h-[300px] min-h-full  object-cover rounded-l-md"
        />
      </div>
      <div className="second w-[56%] p-4">
        <div className="first text-sm mb-4">
          <h3 className="font-semibold h5 mb-2 truncate-2-lines">{title}</h3>
          <IconGroup className="!text-sm">
            <span className="inline-block min-w-3">
              <AddressIcon />
            </span>
            <span className="truncate">{address}</span>
          </IconGroup>
        </div>
        <IconGroup>
          <HomeIcon /> {category}
        </IconGroup>
        <IconGroup className="">
          <ReviewIcon /> {reviews.length} reviews
        </IconGroup>
        <IconGroup className="-ml-[2px] block">
          <SizeIcon />
          <span>{size!.toLocaleString()} sqm</span>
        </IconGroup>
        <div className="last  border-t-[1px] pt-5 border-t-disabled2-main/60 mt-3">
          <h3 className="font-semibold leading-6 mb-1 text-themeColor ">
            {formattedCurrency}
            {price.toLocaleString()}
          </h3>
          {beforePrice && strike ? (
            <span className=" line-through whitespace-nowrap text-secondary-main">
              {formattedCurrency}
              {beforePrice.toLocaleString()}
            </span>
          ) : (
            <span className="text-[13px] whitespace-nowrap text-secondary-main">
              {camelCaseToWords(priceType)}
            </span>
          )}
          {/* <span className="text-[13px] whitespace-nowrap text-secondary-main">
            {camelCaseToWords(priceType)}
          </span>{" "} */}
          <br />
          <span className="text-[13px] block -mt-1 capitalize text-secondary-main">
            <span className="text-themeColor text-[14px]">
              {typeof quotePrice === "number" ? (
                <span>
                  {" "}
                  {formattedCurrency}
                  {quotePrice.toLocaleString()}{" "}
                </span>
              ) : (
                <span>{quotePrice} </span>
              )}
            </span>
            / {camelCaseToWords(quote)}
          </span>
        </div>
      </div>
    </Link>
  );
};
interface ExtendedProperty extends PropertyType {
  className?: string;
}
export const VariantCard: React.FC<ExtendedProperty> = ({
  images,
  title,
  address,
  reviews,
  videoLink,
  size,
  id,
  price,
  beforePrice,
  currency,
  strike,
  type,
  quote,
  quotePrice,
  priceType,
  category,
  className,
}) => {
  const slugwithid = generateSlugWithId(Number(id), title);
  const baseUrl = `/properties/${
    type === "short let" ? "shortlet" : type
  }/${slugwithid}`;

  const formattedCurrency = getCurrencySign(currency);
  // if (!title) {
  //   return;
  // }
  return (
    <Link
      href={baseUrl}
      style={{
        boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
      }}
      className={`flex flex-col h-full sm:mx-auto w-[340px] xs:w-full  text-primary bg-white border overflow-hidden rounded-2xl ${className}`}
    >
      <div className="first group relative w-full">
        <div className="hover group-hover:flex hidden transition-all duration-500 absolute bg-black/60 items-center justify-center z-10 top-0 left-0 w-full h-full">
          <Button className="bg-white !text-themeColor w-max h-max">
            View Details
          </Button>
        </div>
        <div
          className="bottom text-black absolute flex 
        gap-2 bottom-4 right-4"
        >
          <span className="bg-brand-light flex gap-1 items-center rounded-sm p-[0.3rem] py-[0.2rem] text-[8px]">
            <CameraIcon /> +{images?.length}
          </span>
          {videoLink && (
            <span className="bg-brand-light flex gap-2 items-center rounded-sm p-[0.4rem] py-[0.2rem] text-[8px]">
              <VideoIcon />
            </span>
          )}
        </div>
        <div
          className={`tag absolute capitalize text-[11px] w-[300px] text-center left-[-120px] py-1 transform origin-center rotate-[-40deg] top-[5%] ${getTypeColor(
            type
          )}`}
        >
          <span>{type !== "short let" && "For"}</span> {type}
        </div>
        <Image
          height={200}
          width={200}
          src={images[0]}
          alt={title}
          loading="lazy"
          className="w-full h-[200px] object-cover rounded-l-md"
        />
      </div>
      <div className="second w-full p-4">
        <div className="first text-sm mb-2">
          <h3 className="truncate-2-lines h5">{title}</h3>
          <IconGroup className="!text-sm mt-1 ">
            <span className="block">
              {" "}
              <AddressIcon />
            </span>{" "}
            <span className="truncate">{address}</span>
          </IconGroup>
        </div>
        <div className="flex flex-col">
          <div className="item -ml-[2px] flex gap-6">
            <IconGroup>
              <HomeIcon /> {category}
            </IconGroup>
            <IconGroup>
              <SizeIcon /> <span>{size?.toLocaleString()} sqm</span>
            </IconGroup>
          </div>
          <IconGroup>
            <ReviewIcon /> {reviews?.length} reviews
          </IconGroup>
        </div>

        <div className="last flex items-center gap-2  border-t pt-3 border-t-disabled2-main mt-3">
          <div className="">
            <h4 className="font-semibold leading-6 items-end gap-1 flex text-themeColor ">
              {formattedCurrency}
              {price.toLocaleString()}
              {beforePrice && strike ? (
                <span className="text-[13px]  leading-none line-through whitespace-nowrap text-secondary-main">
                  {beforePrice.toLocaleString()}
                </span>
              ) : (
                <span className="text-[13px] leading-none whitespace-nowrap text-secondary-main">
                  {camelCaseToWords(priceType)}
                </span>
              )}
            </h4>
            <span className="text-[13px] capitalize text-secondary-main">
              <span className="text-themeColor">
                {" "}
                <span className="text-themeColor text-[14px]">
                  {typeof quotePrice === "number" ? (
                    <span>
                      {formattedCurrency}
                      {quotePrice.toLocaleString()}{" "}
                    </span>
                  ) : (
                    <span>{camelCaseToWords(quotePrice)}</span>
                  )}
                </span>
              </span>
              / {camelCaseToWords(quote)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export const IconGroup: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <div
      className={`flex gap-2 capitalize whitespace-nowrap text-secondary-main text-base items-center ${className}`}
    >
      {children}
    </div>
  );
};

export const HomeIcon = () => {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.333 2.5H10.6663C10.5779 2.5 10.4932 2.53512 10.4306 2.59763C10.3681 2.66014 10.333 2.74493 10.333 2.83333V2.87267L12.6663 4.73933V2.83333C12.6663 2.74493 12.6312 2.66014 12.5687 2.59763C12.5062 2.53512 12.4214 2.5 12.333 2.5Z"
        fill="#2F364B"
      />
      <path
        d="M7.16699 6.83333C7.16699 6.61232 7.25479 6.40036 7.41107 6.24408C7.56735 6.0878 7.77931 6 8.00033 6C8.22134 6 8.4333 6.0878 8.58958 6.24408C8.74586 6.40036 8.83366 6.61232 8.83366 6.83333C8.83366 7.05435 8.74586 7.26631 8.58958 7.42259C8.4333 7.57887 8.22134 7.66667 8.00033 7.66667C7.77931 7.66667 7.56735 7.57887 7.41107 7.42259C7.25479 7.26631 7.16699 7.05435 7.16699 6.83333Z"
        fill="#2F364B"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M13.833 7.80689L14.3543 8.22423C14.4581 8.30511 14.5896 8.34181 14.7203 8.3264C14.8509 8.31098 14.9703 8.24467 15.0524 8.14186C15.1345 8.03905 15.1728 7.90802 15.1589 7.77717C15.145 7.64633 15.0802 7.52622 14.9783 7.44289L9.56167 3.10956C9.11835 2.7548 8.56747 2.56152 7.99967 2.56152C7.43188 2.56152 6.881 2.7548 6.43767 3.10956L1.02101 7.44289C0.917397 7.52573 0.850935 7.64633 0.836245 7.77817C0.821555 7.91001 0.859839 8.04228 0.942674 8.14589C1.02551 8.24951 1.14611 8.31597 1.27795 8.33066C1.40979 8.34535 1.54206 8.30706 1.64567 8.22423L2.16634 7.80689V14.6669H1.33301C1.2004 14.6669 1.07322 14.7196 0.979454 14.8133C0.885686 14.9071 0.833008 15.0343 0.833008 15.1669C0.833008 15.2995 0.885686 15.4267 0.979454 15.5204C1.07322 15.6142 1.2004 15.6669 1.33301 15.6669H14.6663C14.7989 15.6669 14.9261 15.6142 15.0199 15.5204C15.1137 15.4267 15.1663 15.2995 15.1663 15.1669C15.1663 15.0343 15.1137 14.9071 15.0199 14.8133C14.9261 14.7196 14.7989 14.6669 14.6663 14.6669H13.833V7.80689ZM6.16634 6.83356C6.16634 6.34733 6.35949 5.88102 6.70331 5.5372C7.04713 5.19338 7.51344 5.00023 7.99967 5.00023C8.4859 5.00023 8.95222 5.19338 9.29604 5.5372C9.63985 5.88102 9.83301 6.34733 9.83301 6.83356C9.83301 7.31979 9.63985 7.78611 9.29604 8.12992C8.95222 8.47374 8.4859 8.66689 7.99967 8.66689C7.51344 8.66689 7.04713 8.47374 6.70331 8.12992C6.35949 7.78611 6.16634 7.31979 6.16634 6.83356ZM8.03301 9.33356C8.47567 9.33356 8.85767 9.33356 9.16367 9.37489C9.49167 9.41889 9.80567 9.51823 10.0603 9.77289C10.315 10.0276 10.4143 10.3416 10.4583 10.6696C10.497 10.9562 10.4997 11.3096 10.4997 11.7182V14.6669H9.49967V11.8336C9.49967 11.3482 9.49834 11.0336 9.46767 10.8029C9.43834 10.5862 9.39167 10.5182 9.35301 10.4802C9.31501 10.4422 9.24701 10.3949 9.03034 10.3656C8.79901 10.3349 8.48501 10.3336 7.99967 10.3336C7.51434 10.3336 7.19967 10.3349 6.96901 10.3656C6.75234 10.3949 6.68434 10.4422 6.64634 10.4802C6.60834 10.5182 6.56101 10.5862 6.53167 10.8029C6.50101 11.0342 6.49967 11.3482 6.49967 11.8336V14.6669H5.49967V11.8002C5.49967 11.3576 5.49967 10.9756 5.54101 10.6696C5.58501 10.3416 5.68434 10.0276 5.93901 9.77289C6.19367 9.51823 6.50834 9.41889 6.83567 9.37489C7.14167 9.33356 7.52367 9.33356 7.96701 9.33356H8.03301Z"
        fill="#2F364B"
      />
      <path
        d="M7.16699 6.83333C7.16699 6.61232 7.25479 6.40036 7.41107 6.24408C7.56735 6.0878 7.77931 6 8.00033 6C8.22134 6 8.4333 6.0878 8.58958 6.24408C8.74586 6.40036 8.83366 6.61232 8.83366 6.83333C8.83366 7.05435 8.74586 7.26631 8.58958 7.42259C8.4333 7.57887 8.22134 7.66667 8.00033 7.66667C7.77931 7.66667 7.56735 7.57887 7.41107 7.42259C7.25479 7.26631 7.16699 7.05435 7.16699 6.83333Z"
        fill="#2F364B"
      />
    </svg>
  );
};

export const CameraIcon = () => {
  return (
    <svg
      width="8"
      height="6"
      viewBox="0 0 7 6"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.32954 1.12432H5.10747V0.635498H2.0523V1.12432H0.830236C0.657376 1.12432 0.491596 1.19299 0.369366 1.31522C0.247135 1.43745 0.178467 1.60323 0.178467 1.77609V5.15715C0.178467 5.33001 0.247135 5.49579 0.369366 5.61802C0.491596 5.74025 0.657376 5.80892 0.830236 5.80892H6.32954C6.5024 5.80892 6.66818 5.74025 6.79041 5.61802C6.91264 5.49579 6.98131 5.33001 6.98131 5.15715V1.77609C6.98131 1.60323 6.91264 1.43745 6.79041 1.31522C6.66818 1.19299 6.5024 1.12432 6.32954 1.12432ZM3.57972 5.4423C2.49062 5.4423 1.60486 4.55589 1.60486 3.46678C1.60486 2.37768 2.49062 1.49127 3.57972 1.49127C4.66883 1.49127 5.55524 2.37768 5.55524 3.46678C5.55524 4.55589 4.66883 5.44164 3.57972 5.44164V5.4423ZM3.57972 2.14271C3.40582 2.14274 3.23363 2.17701 3.07297 2.24358C2.91232 2.31015 2.76635 2.40771 2.6434 2.53069C2.52045 2.65367 2.42292 2.79966 2.35639 2.96033C2.28986 3.12101 2.25563 3.29321 2.25565 3.46711C2.25568 3.64101 2.28995 3.8132 2.35652 3.97386C2.42309 4.13451 2.52065 4.28048 2.64363 4.40343C2.76661 4.52639 2.9126 4.62391 3.07328 4.69044C3.23395 4.75697 3.40615 4.7912 3.58005 4.79118C3.9313 4.79113 4.26815 4.65156 4.51649 4.40316C4.76483 4.15475 4.90432 3.81787 4.90428 3.46662C4.90424 3.11537 4.76466 2.77852 4.51626 2.53018C4.26786 2.28184 3.93098 2.14234 3.57972 2.14239V2.14271Z"
        fill="black"
      />
    </svg>
  );
};

export const VideoIcon = () => {
  return (
    <svg
      width="10"
      height="8"
      viewBox="0 0 10 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.62345 1.89348C9.56055 1.85449 9.4887 1.83225 9.41477 1.82891C9.34083 1.82556 9.26727 1.84121 9.20111 1.87437L8.09179 2.42924V2.26325C8.09179 1.54457 7.50694 0.959717 6.78826 0.959717H2.00862C1.28993 0.959717 0.705078 1.54457 0.705078 2.26325V5.73936C0.705078 6.45804 1.28993 7.04289 2.00862 7.04289H6.78826C7.50694 7.04289 8.09179 6.45804 8.09179 5.73936V5.57337L9.20111 6.12781C9.26718 6.16124 9.34075 6.17708 9.41473 6.1738C9.4887 6.17053 9.56059 6.14826 9.62345 6.10913C9.75163 6.02961 9.82985 5.89013 9.82985 5.73936V2.26325C9.82985 2.11248 9.75163 1.973 9.62345 1.89348ZM2.87764 4.65307C2.79066 4.65535 2.70411 4.64019 2.62309 4.60848C2.54206 4.57677 2.4682 4.52916 2.40587 4.46846C2.34354 4.40776 2.29399 4.33519 2.26015 4.25503C2.22631 4.17487 2.20886 4.08875 2.20883 4.00175C2.2088 3.91474 2.22619 3.82861 2.25998 3.74843C2.29377 3.66825 2.34327 3.59564 2.40556 3.5349C2.46785 3.47415 2.54168 3.4265 2.62268 3.39474C2.70368 3.36297 2.79023 3.34775 2.87721 3.34997C3.0471 3.3543 3.20858 3.42482 3.32723 3.54649C3.44588 3.66816 3.51231 3.83136 3.51237 4.00131C3.51242 4.17126 3.4461 4.3345 3.32753 4.45626C3.20896 4.57801 3.04753 4.64863 2.87764 4.65307Z"
        fill="black"
      />
    </svg>
  );
};

export const AddressIcon: React.FC<{ className?: string; color?: string }> = ({
  className,
  color,
}) => {
  return (
    <svg
      className={`${className}`}
      width="11"
      height="14"
      viewBox="0 0 11 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.47843 7.88167C6.48695 7.88167 7.30452 7.0641 7.30452 6.05558C7.30452 5.04706 6.48695 4.22949 5.47843 4.22949C4.46991 4.22949 3.65234 5.04706 3.65234 6.05558C3.65234 7.0641 4.46991 7.88167 5.47843 7.88167Z"
        stroke={color || "#2F364B"}
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.47796 1.18555C4.18647 1.18555 2.94788 1.69859 2.03466 2.61181C1.12144 3.52503 0.608398 4.76362 0.608398 6.05511C0.608398 7.20676 0.853094 7.96033 1.52144 8.79424L5.47796 13.3595L9.43449 8.79424C10.1028 7.96033 10.3475 7.20676 10.3475 6.05511C10.3475 4.76362 9.83449 3.52503 8.92127 2.61181C8.00805 1.69859 6.76945 1.18555 5.47796 1.18555Z"
        stroke={color || "#2F364B"}
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};

export const ReviewIcon = () => {
  return (
    <svg
      width="14"
      height="13"
      viewBox="0 0 18 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.192 10.616H5.266C5.37333 10.616 5.47333 10.5973 5.566 10.56C5.65933 10.5227 5.74933 10.4603 5.836 10.373L10.858 5.346C10.944 5.24733 11.0083 5.144 11.051 5.036C11.0937 4.928 11.1153 4.82067 11.116 4.714C11.1167 4.60733 11.095 4.50167 11.051 4.397C11.0083 4.293 10.946 4.19133 10.864 4.092L9.939 3.142C9.85233 3.056 9.753 2.99167 9.641 2.949C9.52767 2.90633 9.416 2.885 9.306 2.885C9.19933 2.885 9.09267 2.90333 8.986 2.94C8.88 2.976 8.77767 3.04333 8.679 3.142L3.627 8.164C3.54033 8.25067 3.47833 8.34067 3.441 8.434C3.40367 8.52667 3.385 8.62667 3.385 8.734V9.808C3.385 10.04 3.46167 10.2327 3.615 10.386C3.76833 10.5393 3.96067 10.616 4.192 10.616ZM9.3 5.689L8.35 4.744L9.306 3.789L10.231 4.739L9.3 5.689ZM8.816 10.616H14.116C14.258 10.616 14.3767 10.568 14.472 10.472C14.568 10.3767 14.616 10.258 14.616 10.116C14.616 9.97333 14.568 9.85433 14.472 9.759C14.3767 9.66367 14.2577 9.616 14.115 9.616H9.815L8.816 10.616ZM3.077 14L1.373 15.704C1.12033 15.9567 0.828333 16.0147 0.497 15.878C0.165667 15.7413 0 15.493 0 15.133V1.616C0 1.15533 0.154333 0.771 0.463 0.463C0.771667 0.155 1.156 0.000666667 1.616 0H16.385C16.845 0 17.2293 0.154333 17.538 0.463C17.8467 0.771667 18.0007 1.156 18 1.616V12.385C18 12.845 17.846 13.2293 17.538 13.538C17.23 13.8467 16.8457 14.0007 16.385 14H3.077Z"
        fill="#2F364B"
      />
    </svg>
  );
};

export const SizeIcon = () => {
  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.46655 11.7533H8.93322C9.69989 11.7533 10.3266 11.1266 10.3266 10.36V9.89331H8.46655V11.7533Z"
        fill="#2F364B"
      />
      <path
        d="M5.67285 10.3602C5.67285 11.1269 6.29952 11.7536 7.06618 11.7536H7.53285V9.89355H5.67285V10.3602Z"
        fill="#2F364B"
      />
      <path
        d="M5.67285 8.49978V8.96645H7.53285V7.10645H7.06618C6.29952 7.10645 5.67285 7.73311 5.67285 8.49978Z"
        fill="#2F364B"
      />
      <path
        d="M13.353 5.04679L9.51967 2.36012C8.47301 1.62679 6.87301 1.66679 5.86634 2.44679L2.52634 5.05345C1.85301 5.57345 1.33301 6.64012 1.33301 7.48012V12.0801C1.33301 13.7801 2.71301 15.1668 4.40634 15.1668H11.5863C13.2797 15.1668 14.6597 13.7868 14.6597 12.0868V7.56679C14.6663 6.66679 14.0863 5.56012 13.353 5.04679ZM11.253 10.3601C11.253 11.6401 10.2063 12.6868 8.92634 12.6868H7.06634C5.78634 12.6868 4.73967 11.6468 4.73967 10.3601V8.50012C4.73967 7.22012 5.78634 6.17345 7.06634 6.17345H8.92634C10.2063 6.17345 11.253 7.21345 11.253 8.50012V10.3601Z"
        fill="#2F364B"
      />
      <path
        d="M8.93322 7.10669H8.46655V8.96669H10.3266V8.50002C10.3266 7.73336 9.69989 7.10669 8.93322 7.10669Z"
        fill="#2F364B"
      />
    </svg>
  );
};
